<template>
  <div>
    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />用户列表
          <span class="adm-card-title-extra">共 {{ total }} 个账号</span>
        </div>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="query.keyword"
          class="adm-w260"
          name="keyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="昵称 / 邮箱 / 手机号 / 用户ID"
          clearable
          @keyup.enter.native="onSearch"
        />
        <el-select v-model="query.status" class="adm-w140" placeholder="全部状态" clearable>
          <el-option label="正常" value="normal" />
          <el-option label="禁用" value="disabled" />
          <el-option label="已注销" value="deleted" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="onSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onReset">重置</el-button>
      </div>

      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="id" label="用户 ID" width="180">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="nickname" label="昵称" min-width="120" show-overflow-tooltip />
        <el-table-column prop="phone" label="手机号" width="130" />
        <el-table-column prop="email" label="邮箱" min-width="170" show-overflow-tooltip />
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="mini">
              {{ statusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="registerSource" label="来源" width="90" />
        <el-table-column label="注册时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="最近登录" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.lastLoginAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link type="primary" :underline="false" @click="openDetail(row)">详情</el-link>
              <!-- 已注销账号不可编辑：后端 updateProfile 走 findById（带 isNull(deletedAt)），点了必然 9517 -->
              <el-link
                v-if="row.status !== 'deleted'"
                type="primary"
                :underline="false"
                @click="openEdit(row)"
              >编辑</el-link>
              <el-link
                v-if="row.status === 'normal'"
                type="danger"
                :underline="false"
                @click="onToggleStatus(row, 'disabled')"
              >禁用</el-link>
              <el-link
                v-else-if="row.status === 'disabled'"
                type="primary"
                :underline="false"
                @click="onToggleStatus(row, 'normal')"
              >启用</el-link>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="total"
        :page="query.page"
        :size="query.size"
        @page-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <el-dialog title="用户详情" :visible.sync="detailVisible" width="720px" append-to-body>
      <div v-if="detail">
        <div class="adm-desc">
          <div class="adm-desc-item">
            <span class="adm-desc-label">用户 ID</span>
            <span class="adm-desc-value is-mono">{{ detail.id }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">昵称</span>
            <span class="adm-desc-value">{{ detail.nickname }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">手机号</span>
            <span class="adm-desc-value">{{ detail.phone || '-' }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">邮箱</span>
            <span class="adm-desc-value">{{ detail.email || '-' }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">状态</span>
            <span class="adm-desc-value">
              <el-tag :type="statusTagType(detail.status)" size="mini">
                {{ statusText(detail.status) }}
              </el-tag>
            </span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">注册来源</span>
            <span class="adm-desc-value">{{ detail.registerSource || '-' }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">注册 IP</span>
            <span class="adm-desc-value">{{ detail.registerIp || '-' }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">登录 IP</span>
            <span class="adm-desc-value">{{ detail.lastLoginIp || '-' }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">注册时间</span>
            <span class="adm-desc-value">{{ formatTime(detail.createdAt) }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">最近登录</span>
            <span class="adm-desc-value">{{ formatTime(detail.lastLoginAt) }}</span>
          </div>
          <!-- 注销信息：只在已注销账号上出现。purgeAfter 为 null 表示身份信息已匿名化完成 -->
          <div v-if="detail.deletedAt" class="adm-desc-item">
            <span class="adm-desc-label">注销时间</span>
            <span class="adm-desc-value">{{ formatTime(detail.deletedAt) }}</span>
          </div>
          <div v-if="detail.deletedAt" class="adm-desc-item">
            <span class="adm-desc-label">身份信息保留至</span>
            <span class="adm-desc-value">
              {{ detail.purgeAfter ? formatTime(detail.purgeAfter) : '已匿名化完成' }}
            </span>
          </div>
        </div>

        <div class="adm-section-title wsp-entry-row">
          <span>内容统计</span>
          <el-link type="primary" :underline="false" @click="$emit('browse-workspaces', detail.id)">
            查看其工作区 ›
          </el-link>
        </div>
        <div class="adm-stat-grid is-compact">
          <div class="adm-stat">
            <div class="adm-stat-label">随想</div>
            <div class="adm-stat-value adm-num">{{ stats.thoughtCount }}</div>
          </div>
          <div class="adm-stat">
            <div class="adm-stat-label">时间块</div>
            <div class="adm-stat-value adm-num">{{ stats.timeBlockCount }}</div>
          </div>
          <div class="adm-stat">
            <div class="adm-stat-label">工作区</div>
            <div class="adm-stat-value adm-num">{{ stats.workspaceCount }}</div>
          </div>
          <div class="adm-stat">
            <div class="adm-stat-label">好友</div>
            <div class="adm-stat-value adm-num">{{ stats.friendCount }}</div>
          </div>
          <div class="adm-stat">
            <div class="adm-stat-label">工作区分享</div>
            <div class="adm-stat-value adm-num">{{ stats.workspaceShareCount }}</div>
          </div>
          <div class="adm-stat">
            <div class="adm-stat-label">工作表分享</div>
            <div class="adm-stat-value adm-num">{{ stats.projectShareCount }}</div>
          </div>
        </div>
      </div>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="编辑用户资料" :visible.sync="editVisible" width="480px" append-to-body>
      <el-form v-loading="editLoading" label-width="88px">
        <el-form-item label="用户 ID">
          <span class="adm-code">{{ editForm.id }}</span>
        </el-form-item>
        <el-form-item label="昵称">
          <el-input
            v-model="editForm.nickname"
            maxlength="100"
            show-word-limit
            name="editNickname"
            autocomplete="off"
          />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input
            v-model="editForm.phone"
            maxlength="20"
            name="editPhone"
            autocomplete="off"
            placeholder="留空表示不修改"
          />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input
            v-model="editForm.email"
            maxlength="255"
            name="editEmail"
            autocomplete="off"
            placeholder="留空表示不修改"
          />
        </el-form-item>
      </el-form>

      <div class="adm-alert is-warning">
        <i class="el-icon-warning" />
        <span>
          手机号 / 邮箱留空表示不修改（不支持清空，避免把用户锁在门外）。
          被其他账号占用的邮箱 / 手机号会被拒绝。
        </span>
      </div>

      <div slot="footer">
        <el-button size="small" @click="editVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="editSaving" @click="submitEdit">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { fetchUsers, fetchUserDetail, changeUserStatus, updateUser, isOk, isNotLogin } from '@/api/admin'
import AdmPager from '@/components/admin/AdmPager.vue'

const EMPTY_STATS = {
  thoughtCount: 0,
  timeBlockCount: 0,
  workspaceCount: 0,
  friendCount: 0,
  workspaceShareCount: 0,
  projectShareCount: 0
}

/** 空编辑表单。⚠️ Vue 2 只有初始化时就存在的字段才是响应式的，字段必须全部列出。 */
function emptyEditForm() {
  return { id: null, nickname: '', phone: '', email: '' }
}

export default {
  name: 'UsersPanel',
  components: { AdmPager },
  data() {
    return {
      query: { keyword: '', status: '', page: 1, size: 20 },
      rows: [],
      total: 0,
      loading: false,
      detailVisible: false,
      detail: null,
      stats: { ...EMPTY_STATS },
      editVisible: false,
      editSaving: false,
      editLoading: false,
      editForm: emptyEditForm()
    }
  },
  created() {
    this.load()
  },
  methods: {
    load() {
      this.loading = true
      fetchUsers(this.query)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '查询失败')
          }
          this.rows = res.data.list || []
          // total 是 Long，后端序列化成字符串
          this.total = Number(res.data.total || 0)
        })
        .catch(err => {
          if (isNotLogin(err)) return
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSearch() {
      this.query.page = 1
      this.load()
    },
    onReset() {
      this.query = { keyword: '', status: '', page: 1, size: 20 }
      this.load()
    },
    onPageChange(page) {
      this.query.page = page
      this.load()
    },
    onSizeChange(size) {
      this.query.size = size
      this.query.page = 1
      this.load()
    },
    openDetail(row) {
      this.openDetailById(row.id)
    },
    /**
     * 按 ID 拉详情并弹窗。AdminPage 从工作区浏览反向跳回来时用
     * （$refs 调用）—— 那条路径只有 userId，没有列表行对象。
     */
    openDetailById(id) {
      fetchUserDetail(id)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '获取详情失败')
          }
          this.detail = res.data
          this.stats = res.data.stats || { ...EMPTY_STATS }
          this.detailVisible = true
        })
        .catch(err => {
          this.$message.error(err.message || '获取详情失败')
        })
    },
    onToggleStatus(row, status) {
      const disabling = status === 'disabled'
      const action = disabling ? '禁用' : '启用'
      const tip = disabling
        ? `禁用后该用户将立即被踢下线，且无法再登录。确定禁用「${row.nickname}」吗？`
        : `确定启用「${row.nickname}」吗？`
      this.$confirm(tip, `${action}用户`, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: disabling ? 'warning' : 'info'
      })
        .then(() => changeUserStatus(row.id, status))
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : `${action}失败`)
          }
          this.$message.success(`${action}成功`)
          this.load()
        })
        .catch(err => {
          // $confirm 取消时抛的是字符串 'cancel'，不弹错
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || `${action}失败`)
        })
    },
    /**
     * 打开编辑弹窗。
     *
     * <p>⚠️ 必须拉一次详情，不能用列表行回填：列表接口的手机号/邮箱已脱敏
     * （`138****8000`），拿去回填会在保存时把掩码写进库。详情接口返回的是完整值。
     * 先清空表单再请求，避免上一次的残留值在慢请求期间被误提交。</p>
     */
    openEdit(row) {
      this.editForm = { id: row.id, nickname: row.nickname || '', phone: '', email: '' }
      this.editVisible = true
      this.editLoading = true
      fetchUserDetail(row.id)
        .then(res => {
          if (!isOk(res)) {
            throw new Error(res && res.message ? res.message : '获取用户资料失败')
          }
          const detail = res.data || {}
          this.editForm = {
            id: row.id,
            nickname: detail.nickname || row.nickname || '',
            phone: detail.phone || '',
            email: detail.email || ''
          }
        })
        .catch(err => {
          this.editVisible = false
          this.$message.error(err.message || '获取用户资料失败')
        })
        .finally(() => {
          this.editLoading = false
        })
    },
    submitEdit() {
      if (this.editSaving || this.editLoading) return
      const nickname = this.editForm.nickname.trim()
      if (!nickname) {
        this.$message.error('昵称不能为空')
        return
      }
      const phone = this.editForm.phone.trim()
      const email = this.editForm.email.trim()
      if (phone && !/^1[3-9]\d{9}$/.test(phone)) {
        this.$message.error('手机号格式不正确')
        return
      }
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.$message.error('邮箱格式不正确')
        return
      }

      // 留空 = 不修改，所以这里原样传空串，后端会保留原值
      this.editSaving = true
      updateUser(this.editForm.id, { nickname, phone, email })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '保存失败')
          }
          this.$message.success('已保存')
          this.editVisible = false
          this.load()
        })
        .catch(err => {
          this.$message.error((err && err.message) || '保存失败')
        })
        .finally(() => {
          this.editSaving = false
        })
    },
    statusText(status) {
      if (status === 'normal') return '正常'
      if (status === 'disabled') return '禁用'
      if (status === 'deleted') return '已注销'
      return status || '-'
    },
    statusTagType(status) {
      if (status === 'normal') return 'success'
      if (status === 'disabled') return 'danger'
      return 'info'
    },
    formatTime(value) {
      if (!value) return '-'
      // 后端 LocalDateTime 序列化为 ISO 串（2026-10-08T11:49:28.123）
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
/* 卡片 / 筛选栏 / 分页 / 描述列表样式统一来自 @/styles/admin.css */
.adm-w260 {
  width: 260px;
}

.adm-w140 {
  width: 140px;
}

/* 弹窗内的小统计卡：比概览页的更紧凑 */
.adm-stat-grid.is-compact {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

/* 小节标题行：左「内容统计」+ 右「查看其工作区」入口 */
.wsp-entry-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.adm-stat-grid.is-compact .adm-stat {
  padding: 12px 14px;
}

.adm-stat-grid.is-compact .adm-stat-value {
  margin-top: 6px;
  font-size: 20px;
}
</style>
